import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge, type BadgeTon } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconPlus, IconWartung } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatDatum, formatEuro, parseEuroEingabe } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

type Status = 'offen' | 'beauftragt' | 'erledigt';

interface Massnahme {
  id: string;
  objektId: string;
  objektBezeichnung?: string;
  anlageBezeichnung?: string;
  dienstleisterId: string | null;
  dienstleisterName?: string | null;
  titel: string;
  beschreibung: string | null;
  status: Status;
  faelligAm: string | null;
  kostenCent: number | null;
  umlagefaehig: boolean;
  erledigtAm: string | null;
  belegId: string | null;
}

interface Vorschlag {
  pflichtId: string;
  anlageId: string | null;
  objektId: string;
  objektBezeichnung: string | null;
  anlageBezeichnung: string | null;
  titel: string;
  kostenartCode: string | null;
  faelligAm: string | null;
}

interface DL {
  id: string;
  name: string;
}

const STATUS_LABEL: Record<Status, string> = {
  offen: 'Offen',
  beauftragt: 'Beauftragt',
  erledigt: 'Erledigt',
};
const STATUS_TON: Record<Status, BadgeTon> = {
  offen: 'warnung',
  beauftragt: 'info',
  erledigt: 'erfolg',
};

const DEMO_MASSNAHMEN: Massnahme[] = [
  {
    id: 'ma-1',
    objektId: 'obj-1',
    objektBezeichnung: 'Lindenstraße 14',
    anlageBezeichnung: 'Aufzug',
    dienstleisterId: 'dl-1',
    dienstleisterName: 'Aufzugstechnik Nord',
    titel: 'Wiederkehrende Prüfung Aufzug (ZÜS)',
    beschreibung: null,
    status: 'beauftragt',
    faelligAm: '2026-11-30',
    kostenCent: null,
    umlagefaehig: true,
    erledigtAm: null,
    belegId: null,
  },
  {
    id: 'ma-2',
    objektId: 'obj-1',
    objektBezeichnung: 'Lindenstraße 14',
    anlageBezeichnung: 'Heizung',
    dienstleisterId: null,
    dienstleisterName: null,
    titel: 'Schornsteinfeger – Kehrung und Überprüfung',
    beschreibung: null,
    status: 'erledigt',
    faelligAm: '2026-03-15',
    kostenCent: 8900,
    umlagefaehig: true,
    erledigtAm: '2026-03-12',
    belegId: null,
  },
];

const DEMO_VORSCHLAEGE: Vorschlag[] = [
  {
    pflichtId: 'p-1',
    anlageId: 'a-1',
    objektId: 'obj-1',
    objektBezeichnung: 'Lindenstraße 14',
    anlageBezeichnung: 'Trinkwasseranlage',
    titel: 'Legionellenuntersuchung (TrinkwV)',
    kostenartCode: 'wasser',
    faelligAm: '2026-10-01',
  },
  {
    pflichtId: 'p-2',
    anlageId: 'a-2',
    objektId: 'obj-2',
    objektBezeichnung: 'Am Wasserturm 3',
    anlageBezeichnung: 'Blitzschutz',
    titel: 'Prüfung Blitzschutzanlage',
    kostenartCode: null,
    faelligAm: '2026-12-01',
  },
];

const DEMO_DL: DL[] = [
  { id: 'dl-1', name: 'Aufzugstechnik Nord' },
  { id: 'dl-2', name: 'Heizung & Sanitär Berg' },
];

export function Massnahmen() {
  const [massnahmen, setMassnahmen] = useState<Massnahme[]>(DEMO ? DEMO_MASSNAHMEN : []);
  const [vorschlaege, setVorschlaege] = useState<Vorschlag[]>(DEMO ? DEMO_VORSCHLAEGE : []);
  const [dienstleister, setDienstleister] = useState<DL[]>(DEMO ? DEMO_DL : []);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    setLaden(true);
    Promise.all([
      api.get<{ massnahmen: Massnahme[] }>('/massnahmen'),
      api.get<{ vorschlaege: Vorschlag[] }>('/massnahmen/vorschlaege'),
      api.get<{ dienstleister: DL[] }>('/dienstleister').catch(() => ({ dienstleister: [] })),
    ])
      .then(([m, v, d]) => {
        setMassnahmen(m.massnahmen);
        setVorschlaege(v.vorschlaege);
        setDienstleister(d.dienstleister);
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Maßnahmen konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  async function uebernehmen(v: Vorschlag) {
    const neu: Massnahme = {
      id: `neu-${Date.now()}`,
      objektId: v.objektId,
      objektBezeichnung: v.objektBezeichnung ?? undefined,
      anlageBezeichnung: v.anlageBezeichnung ?? undefined,
      dienstleisterId: null,
      titel: v.titel,
      beschreibung: null,
      status: 'offen',
      faelligAm: v.faelligAm,
      kostenCent: null,
      umlagefaehig: true,
      erledigtAm: null,
      belegId: null,
    };
    if (!DEMO) {
      try {
        const r = await api.post<{ massnahme: Massnahme }>('/massnahmen', {
          objektId: v.objektId,
          anlageId: v.anlageId ?? undefined,
          pflichtId: v.pflichtId,
          kostenartCode: v.kostenartCode ?? undefined,
          titel: v.titel,
          faelligAm: v.faelligAm ?? undefined,
        });
        setMassnahmen((xs) => [r.massnahme, ...xs]);
      } catch (e) {
        setFehler(e instanceof ApiFehler ? e.message : 'Die Maßnahme konnte nicht angelegt werden.');
        return;
      }
    } else {
      setMassnahmen((xs) => [neu, ...xs]);
    }
    setVorschlaege((xs) => xs.filter((x) => x.pflichtId !== v.pflichtId));
  }

  async function aktualisiere(id: string, aenderung: Partial<Massnahme>) {
    setMassnahmen((xs) => xs.map((m) => (m.id === id ? { ...m, ...aenderung } : m)));
    if (DEMO) return;
    try {
      await api.patch(`/massnahmen/${id}`, {
        status: aenderung.status,
        dienstleisterId: aenderung.dienstleisterId,
        kostenCent: aenderung.kostenCent,
        umlagefaehig: aenderung.umlagefaehig,
      });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Änderung konnte nicht gespeichert werden.');
    }
  }

  async function alsBeleg(id: string) {
    if (DEMO) {
      setMassnahmen((xs) => xs.map((m) => (m.id === id ? { ...m, belegId: `beleg-${Date.now()}` } : m)));
      return;
    }
    try {
      const r = await api.post<{ belegId: string }>(`/massnahmen/${id}/beleg`);
      setMassnahmen((xs) => xs.map((m) => (m.id === id ? { ...m, belegId: r.belegId } : m)));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Der Beleg konnte nicht gebucht werden.');
    }
  }

  const offen = massnahmen.filter((m) => m.status !== 'erledigt');
  const erledigt = massnahmen.filter((m) => m.status === 'erledigt');

  return (
    <div>
      <Seitenkopf
        titel="Maßnahmen"
        beschreibung="Arbeitsaufträge aus fälligen Prüf- und Wartungspflichten: beauftragen, an Dienstleister zuweisen und nach Erledigung als Beleg in die Abrechnung überführen."
      />

      <div className="mb-4 flex justify-end">
        <Button icon={<IconPlus groesse={16} />} groesse="sm" onClick={() => setNeuOffen((o) => !o)}>
          Maßnahme
        </Button>
      </div>

      {neuOffen && (
        <NeuFormular
          onAngelegt={(m) => {
            setMassnahmen((xs) => [m, ...xs]);
            setNeuOffen(false);
          }}
          onAbbruch={() => setNeuOffen(false)}
        />
      )}

      {fehler && (
        <Card className="mb-4">
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      )}

      {laden ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : (
        <div className="space-y-6">
          {/* Vorschläge aus fälligen Pflichten */}
          {vorschlaege.length > 0 && (
            <section>
              <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                Vorgeschlagen aus fälligen Pflichten ({vorschlaege.length})
              </h2>
              <div className="space-y-2.5">
                {vorschlaege.map((v) => (
                  <Card key={v.pflichtId}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-fg">{v.titel}</span>
                          {v.anlageBezeichnung && <Badge ton="neutral">{v.anlageBezeichnung}</Badge>}
                        </div>
                        <p className="mt-1 text-xs text-fg-subtle">
                          {v.objektBezeichnung}
                          {v.faelligAm ? ` · fällig ${formatDatum(v.faelligAm)}` : ''}
                        </p>
                      </div>
                      <Button variante="sekundaer" groesse="sm" onClick={() => uebernehmen(v)}>
                        Als Maßnahme übernehmen
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* Offene Maßnahmen */}
          <section>
            <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              Offen ({offen.length})
            </h2>
            {offen.length === 0 ? (
              <EmptyState
                icon={<IconWartung groesse={22} />}
                titel="Keine offenen Maßnahmen"
                beschreibung="Fällige Prüfpflichten erscheinen oben als Vorschlag. Sie können auch manuell eine Maßnahme anlegen."
              />
            ) : (
              <div className="space-y-2.5">
                {offen.map((m) => (
                  <MassnahmeKarte key={m.id} m={m} dienstleister={dienstleister} onAendern={aktualisiere} onBeleg={alsBeleg} />
                ))}
              </div>
            )}
          </section>

          {/* Erledigt */}
          {erledigt.length > 0 && (
            <section>
              <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                Erledigt ({erledigt.length})
              </h2>
              <div className="space-y-2.5">
                {erledigt.map((m) => (
                  <MassnahmeKarte key={m.id} m={m} dienstleister={dienstleister} onAendern={aktualisiere} onBeleg={alsBeleg} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function MassnahmeKarte({
  m,
  dienstleister,
  onAendern,
  onBeleg,
}: {
  m: Massnahme;
  dienstleister: DL[];
  onAendern: (id: string, a: Partial<Massnahme>) => void;
  onBeleg: (id: string) => void;
}) {
  const [kosten, setKosten] = useState(m.kostenCent != null ? (m.kostenCent / 100).toString().replace('.', ',') : '');

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{m.titel}</span>
            <Badge ton={STATUS_TON[m.status]}>{STATUS_LABEL[m.status]}</Badge>
            {m.anlageBezeichnung && <Badge ton="neutral">{m.anlageBezeichnung}</Badge>}
            {!m.umlagefaehig && <Badge ton="warnung">nicht umlagefähig</Badge>}
            {m.belegId && <Badge ton="erfolg">als Beleg gebucht</Badge>}
          </div>
          <p className="mt-1 text-xs text-fg-subtle">
            {m.objektBezeichnung}
            {m.faelligAm ? ` · fällig ${formatDatum(m.faelligAm)}` : ''}
            {m.erledigtAm ? ` · erledigt ${formatDatum(m.erledigtAm)}` : ''}
            {m.dienstleisterName ? ` · ${m.dienstleisterName}` : ''}
          </p>
        </div>
        {m.kostenCent != null && (
          <span className="tabellenziffern text-sm font-semibold text-fg">{formatEuro(m.kostenCent)}</span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <select
          className={`${feldKlasse} max-w-[9rem]`}
          value={m.status}
          onChange={(e) => onAendern(m.id, { status: e.target.value as Status })}
        >
          <option value="offen">Offen</option>
          <option value="beauftragt">Beauftragt</option>
          <option value="erledigt">Erledigt</option>
        </select>

        <select
          className={`${feldKlasse} max-w-[12rem]`}
          value={m.dienstleisterId ?? ''}
          onChange={(e) => onAendern(m.id, { dienstleisterId: e.target.value || null })}
        >
          <option value="">Dienstleister wählen …</option>
          {dienstleister.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-1">
          <input
            className={`${feldKlasse} w-28`}
            inputMode="decimal"
            placeholder="Kosten €"
            value={kosten}
            onChange={(e) => setKosten(e.target.value)}
            onBlur={() => {
              const c = parseEuroEingabe(kosten);
              onAendern(m.id, { kostenCent: c });
            }}
          />
        </div>

        {m.status === 'erledigt' && m.kostenCent != null && !m.belegId && (
          <Button variante="sekundaer" groesse="sm" onClick={() => onBeleg(m.id)}>
            Als Beleg buchen
          </Button>
        )}
      </div>
    </Card>
  );
}

function NeuFormular({
  onAngelegt,
  onAbbruch,
}: {
  onAngelegt: (m: Massnahme) => void;
  onAbbruch: () => void;
}) {
  const [objekte, setObjekte] = useState<{ id: string; bezeichnung: string }[]>([]);
  const [objektId, setObjektId] = useState('');
  const [titel, setTitel] = useState('');
  const [faellig, setFaellig] = useState('');
  const [umlagefaehig, setUmlagefaehig] = useState(true);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) {
      setObjekte([
        { id: 'obj-1', bezeichnung: 'Lindenstraße 14' },
        { id: 'obj-2', bezeichnung: 'Am Wasserturm 3' },
      ]);
      setObjektId('obj-1');
      return;
    }
    api
      .get<{ objekte: { id: string; bezeichnung: string }[] }>('/objekte')
      .then((d) => {
        setObjekte(d.objekte);
        setObjektId((g) => g || (d.objekte[0]?.id ?? ''));
      })
      .catch(() => setFehler('Objekte konnten nicht geladen werden.'));
  }, []);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!objektId || !titel.trim()) {
      setFehler('Bitte Objekt und Titel angeben.');
      return;
    }
    setSendet(true);
    const objektBezeichnung = objekte.find((o) => o.id === objektId)?.bezeichnung;
    try {
      if (!DEMO) {
        const r = await api.post<{ massnahme: Massnahme }>('/massnahmen', {
          objektId,
          titel: titel.trim(),
          faelligAm: faellig || undefined,
          umlagefaehig,
        });
        onAngelegt(r.massnahme);
      } else {
        onAngelegt({
          id: `neu-${Date.now()}`,
          objektId,
          objektBezeichnung,
          dienstleisterId: null,
          titel: titel.trim(),
          beschreibung: null,
          status: 'offen',
          faelligAm: faellig || null,
          kostenCent: null,
          umlagefaehig,
          erledigtAm: null,
          belegId: null,
        });
      }
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Maßnahme konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Neue Maßnahme" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
            <select className={feldKlasse} value={objektId} onChange={(e) => setObjektId(e.target.value)}>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
          <Input label="Fällig am (optional)" type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} />
        </div>
        <Input label="Titel" placeholder="z. B. Wartung Heizung beauftragen" value={titel} onChange={(e) => setTitel(e.target.value)} />
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" checked={umlagefaehig} onChange={(e) => setUmlagefaehig(e.target.checked)} />
          Kosten sind umlagefähig
        </label>
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Maßnahme anlegen'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Massnahmen;
